import { RunStatus } from '@aero-platform/shared/generated/AeroStructures'
import { Button } from 'primereact/button'
import { Column } from 'primereact/column'
import { DataTable } from 'primereact/datatable'
import { Dialog } from 'primereact/dialog'
import { InputTextarea } from 'primereact/inputtextarea'
import { Panel } from 'primereact/panel'
import { classNames } from 'primereact/utils'
import { useRef, useState } from 'react'
import { toast } from 'react-hot-toast'
import { useRecoilValue } from 'recoil'
import { WorkflowStatusDef } from '../../utils/constants'
import { formatDateWithLocale } from '../../utils/intl'
import { currentTeamState, currentUserState } from '../../utils/state-atoms'
import { CollapseIcon, CopyIcon, EditIcon, ExpandIcon } from '../Icons'
import IconButton from '../buttons/IconButton'
import { RunStatusCircle } from './StatusCircle'

const columns = [
  {
    header: 'Name',
    field: 'name',
    headerClassName: '',
    bodyClassName: 'font-bold',
    body: rowData => {
      return (
        <div className="flex items-center space-x-2 cursor-pointer">
          <>{rowData.partRef.name}</>
        </div>
      )
    }
  },
  {
    header: 'PID',
    field: 'partRef.pid'
  },
  {
    header: 'Type',
    field: 'partRef.type'
  }
]

const RunDetailsPanel = ({
  submission,
  setShowEditModal,
  verifiedStatus,
  onNotesChange,
  onDescriptionChange
}) => {
  const refDetails = useRef<Panel>(null)
  const [showDetails, setShowDetails] = useState(false)
  const [showCopy, setShowCopy] = useState(false)
  const [showNotesModal, setShowNotesModal] = useState(false)
  const [editingNotes, setEditingNotes] = useState('')
  const [showDescriptionModal, setShowDescriptionModal] = useState(false)
  const [editingDescription, setEditingDescription] = useState('')
  const currentUser = useRecoilValue(currentUserState)
  const currentTeam = useRecoilValue(currentTeamState)

  const openNotesModal = () => {
    setEditingNotes(submission.notes || '')
    setShowNotesModal(true)
  }

  const saveNotes = async () => {
    if (!onNotesChange) {
      setShowNotesModal(false)
      return
    }

    try {
      await onNotesChange(editingNotes)
      setShowNotesModal(false)
    } catch (err) {
      console.error(err)
    }
  }

  const openDescriptionModal = () => {
    setEditingDescription(submission.runConfig?.description || '')
    setShowDescriptionModal(true)
  }

  const saveDescription = async () => {
    if (!onDescriptionChange) {
      setShowDescriptionModal(false)
      return
    }

    try {
      await onDescriptionChange(editingDescription)
      setShowDescriptionModal(false)
    } catch (err) {
      console.error(err)
    }
  }

  return (
    <Panel
      ref={refDetails}
      toggleable
      collapsed={!showDetails}
      header={
        <div className="flex items-center space-x-1 min-w-full">
          <Button
            link
            className="icon-button bg-transparent"
            onClick={evt => {
              refDetails.current?.toggle(evt)
            }}>
            {!showDetails && <ExpandIcon className="fill-dark-blue dark:fill-neutral-10 h-6 w-6" />}
            {showDetails && (
              <CollapseIcon className="fill-dark-blue dark:fill-neutral-10 h-6 w-6" />
            )}
          </Button>
          <span className="text-gm-primary-90 dark:text-light-blue text-lg">Run Details</span>
          <div className="text-gm-primary-90 dark:text-light-blue text-lg">-</div>
          <div
            className="flex items-center space-x-1"
            onMouseEnter={() => setShowCopy(true)}
            onMouseLeave={() => setShowCopy(false)}>
            <span className="text-gm-primary-90 dark:text-light-blue hover:text-gm-blue text-lg">
              {submission.name}
            </span>
            {showCopy && (
              <IconButton
                clear
                title="Copy"
                icon={<CopyIcon className="fill-gm-blue w-4 h-4" />}
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(submission.name)
                    toast.success('Run ID copied to clipboard.')
                  } catch (err) {
                    toast.error('Failed to copy.')
                  }
                }}></IconButton>
            )}
          </div>
          <div className="flex items-center space-x-1 justify-end grow pr-2">
            {submission && submission.overallStatus === RunStatus.CREATED && showDetails && (
              <IconButton
                title="Edit"
                severity="secondary"
                icon={<EditIcon className="fill-neutral-10 dark:fill-neutral-10 h-4 w-4" />}
                onClick={() => setShowEditModal(true)}></IconButton>
            )}
          </div>
        </div>
      }
      onExpand={e => setShowDetails(true)}
      onCollapse={e => setShowDetails(false)}
      pt={{
        root: {
          className: 'rounded-lg shadow-md dark:border-neutral-90'
        },
        toggler: {
          className: 'hidden'
        },
        header: {
          className: classNames('bg-alt-gray dark:bg-neutral-80 dark:border-neutral-90 !p-2', {
            'rounded-b-none': showDetails,
            'rounded-b-md': !showDetails
          })
        },
        title: {
          className: 'grow'
        },
        content: { className: 'p-4 dark:bg-neutral-80 dark:border-neutral-90' }
      }}>
      <div className=" grid grid-cols-2 ">
        <div className="grid grid-cols-2 gap-y-4 auto-rows-min">
          <div className="grid grid-flow-col justify-start">
            <div className="flex justify-center items-center ">
              {' '}
              {/* border-solid border-2 border-black*/}
              <RunStatusCircle status={verifiedStatus ?? submission.overallStatus} />
            </div>
            <div className="flex flex-col ">
              <div className="font-bold">Status</div>
              <div>{WorkflowStatusDef[verifiedStatus ?? submission.overallStatus].name}</div>
            </div>
          </div>

          <div className="">
            <div className="font-bold flex items-center space-x-2">
              <span>Description</span>
              <IconButton
                clear
                title="Edit Description"
                icon={<EditIcon className="fill-gm-blue dark:fill-light-blue w-4 h-4" />}
                onClick={openDescriptionModal}
              />
            </div>
            <div className="whitespace-pre-wrap break-words">
              {submission.runConfig.description || 'N/A'}
            </div>
          </div>

          <div className="col-span-2">
            <div className="font-bold flex items-center space-x-2">
              <span>Notes</span>
              <IconButton
                clear
                title="Edit Notes"
                icon={<EditIcon className="fill-gm-blue dark:fill-light-blue w-4 h-4" />}
                onClick={openNotesModal}
              />
            </div>
            {submission.notes ? (
              <div className="whitespace-pre-wrap break-words">
                {submission.notes.replace(/\\r\\n/g, '\n').replace(/\r\n/g, '\n')}
              </div>
            ) : (
              <div>N/A</div>
            )}
          </div>

          <div className="">
            <div className="font-bold">Methodology Set</div>
            <div>{submission.runConfig.selectedMethodSet?.name}</div>
            {submission.runConfig.selectedMethodSet?.description && (
              <div className="text-sm text-gray-400 mr-2">
                {submission.runConfig.selectedMethodSet.description}
              </div>
            )}
          </div>

          <div className="">
            <div className="font-bold">Master Geometry</div>
            <div>{submission.runConfig.baselineRunName || 'None'}</div>
          </div>

          <div className="">
            <div className="font-bold">Reference Run</div>
            <div>{submission.runConfig.referenceRunName || 'None'}</div>
          </div>

          <div className="">
            <div className="font-bold">Attitude Set</div>
            <div>{submission.runConfig.selectedAttitudeSet?.name || 'N/A'}</div>
          </div>

          <div className="">
            <div className="font-bold">Workflow Template</div>
            <div>{submission.runConfig.workflowSubmissionTemplate?.name || 'N/A'}</div>
          </div>

          <div className="">
            <div className="font-bold">Sim Template</div>
            <div>{submission.runConfig.overrideSelectedSimFile || 'N/A'}</div>
          </div>

          <div className="">
            <div className="font-bold">Geometry Staging Files</div>
            <div>
              {submission.runConfig.geoStagingFiles.length > 0
                ? submission.runConfig.geoStagingFiles.join(', ')
                : 'N/A'}
            </div>
          </div>

          <div>
            <div className="font-bold">Pressure Tap Files</div>
            <div>
              {submission.runConfig.selectedPtapLocationSet
                ? submission.runConfig.selectedPtapLocationSet?.name +
                  '/' +
                  submission.runConfig.selectedPtapLocationSet?.version
                : 'N/A'}
            </div>
          </div>

          <div>
            <div className="font-bold">Ride Height Files</div>
            <div>
              {submission.runConfig.selectedRHRepoSet
                ? submission.runConfig.selectedRHRepoSet?.name +
                  '/' +
                  submission.runConfig.selectedRHRepoSet?.version
                : 'N/A'}
            </div>
          </div>

          <div>
            <div className="font-bold">Cores Count</div>
            <div>
              {submission.runConfig.coresCount !== undefined &&
              submission.runConfig.coresCount !== 0
                ? submission.runConfig.coresCount
                : 'N/A'}
            </div>
          </div>

          <div>
            <div className="font-bold">Walltime Override (hours)</div>
            <div>
              {submission.runConfig.walltimeOverride !== undefined &&
              submission.runConfig.walltimeOverride !== 0
                ? submission.runConfig.walltimeOverride
                : 'N/A'}
            </div>
          </div>

          {submission.runConfig.followingCars && submission.runConfig.followingCars.length > 0 && (
            <div className="col-span-2">
              <div className="font-bold">Trailing Car Configuration</div>
              <ul className="mt-2">
                <li className="flex items-start space-x-2">
                  <span className="font-semibold w-10">#</span>
                  <span className="font-semibold w-24">Length(m)</span>
                  <span className="font-semibold w-24">Width(m)</span>
                </li>
                {submission.runConfig.followingCars.map((display, idx) => (
                  <li key={idx} className="flex items-start space-x-2">
                    <span className="w-10">{idx + 1}</span>
                    <span className="w-24">
                      {Number(display.localPosition.distance_x_m).toFixed(3)}
                    </span>
                    <span className="w-24">
                      {Number(display.localPosition.distance_y_m).toFixed(3)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="">
            <div className="font-bold">Created by</div>
            <div>{submission.createByUserName}</div>
          </div>

          <div className="">
            <div className="font-bold">Date Created</div>
            <div>{formatDateWithLocale(submission.timeCreated, currentUser.locale)}</div>
          </div>

          {currentTeam === 'NASCAR-Cup' && (
            <div className="">
              <div className="font-bold">Compliance Team</div>
              <div>{submission.runConfig.complianceTeam}</div>
            </div>
          )}

          {currentTeam === 'NASCAR-Cup' && (
            <div className="">
              <div className="font-bold">Compliance Description</div>
              <div>{submission.runConfig.complianceDesc}</div>
            </div>
          )}
        </div>

        <div>
          {submission.runConfig.selectedMethodSet?.starccmConfig && (
            <div className="mt-4">
              <div className="font-bold mb-2">StarCCM+ Configuration</div>
              <div className="text-sm space-y-2">
                {(submission.runConfig.selectedMethodSet.starccmConfig.stepOrder?.length > 0 ||
                  submission.runConfig.selectedMethodSet.starccmConfig.macroSteps?.length > 0) && (
                  <div>
                    <div className="font-semibold">Macro Steps</div>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {(submission.runConfig.selectedMethodSet.starccmConfig.stepOrder?.length > 0
                        ? submission.runConfig.selectedMethodSet.starccmConfig.stepOrder
                        : submission.runConfig.selectedMethodSet.starccmConfig.macroSteps.map(
                            s => s.name || s.value
                          )
                      ).map((step, idx) => (
                        <span
                          key={idx}
                          className="inline-block bg-gm-blue text-white text-xs px-2 py-1 rounded">
                          {step}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {submission.runConfig.selectedMethodSet.starccmConfig.starccmParameters?.name && (
                  <div>
                    <span className="font-semibold">StarCCM Preset: </span>
                    {submission.runConfig.selectedMethodSet.starccmConfig.starccmParameters.name}
                  </div>
                )}
                {submission.runConfig.selectedMethodSet.starccmConfig.miscellaneousParameters
                  ?.name && (
                  <div>
                    <span className="font-semibold">Misc Preset: </span>
                    {
                      submission.runConfig.selectedMethodSet.starccmConfig.miscellaneousParameters
                        .name
                    }
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <Dialog
        visible={showNotesModal}
        onHide={() => setShowNotesModal(false)}
        header="Edit Notes"
        modal
        style={{ width: '50vw' }}
        breakpoints={{ '960px': '75vw', '640px': '90vw' }}>
        <div className="flex flex-col space-y-4">
          <div>
            <label htmlFor="run-notes" className="block text-sm font-medium mb-2">
              Notes
            </label>
            <InputTextarea
              id="run-notes"
              value={editingNotes}
              onChange={e => setEditingNotes(e.target.value)}
              rows={5}
              className="w-full"
            />
          </div>
          <div className="flex justify-end space-x-2">
            <Button
              label="Cancel"
              severity="secondary"
              onClick={() => setShowNotesModal(false)}
              pt={{
                root: {
                  className: 'bg-gray-600 dark:!bg-white'
                },
                label: {
                  className: 'text-white dark:!text-black'
                }
              }}
            />
            <Button label="Save" onClick={saveNotes} />
          </div>
        </div>
      </Dialog>

      <Dialog
        visible={showDescriptionModal}
        onHide={() => setShowDescriptionModal(false)}
        header="Edit Description"
        modal
        style={{ width: '50vw' }}
        breakpoints={{ '960px': '75vw', '640px': '90vw' }}>
        <div className="flex flex-col space-y-4">
          <div>
            <label htmlFor="run-description" className="block text-sm font-medium mb-2">
              Description
            </label>
            <InputTextarea
              id="run-description"
              value={editingDescription}
              onChange={e => setEditingDescription(e.target.value)}
              rows={5}
              className="w-full"
            />
          </div>
          <div className="flex justify-end space-x-2">
            <Button
              label="Cancel"
              severity="secondary"
              onClick={() => setShowDescriptionModal(false)}
              pt={{
                root: {
                  className: 'bg-gray-600 dark:!bg-white'
                },
                label: {
                  className: 'text-white dark:!text-black'
                }
              }}
            />
            <Button label="Save" onClick={saveDescription} />
          </div>
        </div>
      </Dialog>
    </Panel>
  )
}

export default RunDetailsPanel
